/*
 * The review room's one clock: the position in the round, in seconds from the
 * timeline's origin, shared by the player, the scrubber, the lanes and the queue.
 *
 * The address holds it: ?view=proctoring&t=mm:ss[&item=key], other params kept.
 *  - an explicit seek, pick or chapter is a new history entry (push), so Back
 *    returns to the moment before;
 *  - a pause (or a segment reaching its end) rewrites the current entry (replace);
 *  - playback never writes the address;
 *  - Back / Forward change `t` under the room, which re-seeks to it.
 *
 * The active media element drives the clock: the player registers a driver
 * (seek/play/pause/setRate) and reports its position (timeupdate plus a ~10 Hz
 * animation frame). With nothing recorded, a timer moves the clock instead.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { clockOf, parseClock } from "./rules/format";
import { LEAD } from "./rules/proctoring";

export const VIEW = "proctoring";
const TICK_MS = 100;

/**
 * The search string with some params set (null removes one); every other param is kept.
 * `:` is left as it is so the address reads t=04:32.
 * @param {string} search
 * @param {Object<string, string|null|undefined>} set
 * @returns {string} "" or "?a=b"
 */
export function searchWith(search, set) {
  const p = new URLSearchParams(search || "");
  Object.entries(set).forEach(([k, v]) => {
    if (v === undefined) return;
    if (v === null) p.delete(k);
    else p.set(k, v);
  });
  const s = p.toString().replace(/%3A/gi, ":");
  return s ? `?${s}` : "";
}

/** The room's address params, read from a search string. */
export function readAddress(search) {
  const p = new URLSearchParams(search || "");
  return { open: p.get("view") === VIEW, t: parseClock(p.get("t")), item: p.get("item") };
}

const ClockContext = createContext(null);

/** The clock, inside ReviewClockProvider. */
export function useReviewClock() {
  const c = useContext(ClockContext);
  if (!c) throw new Error("useReviewClock outside ReviewClockProvider");
  return c;
}

const clamp = (x, lo, hi) => Math.max(lo, Math.min(hi, x));
const isNum = (x) => typeof x === "number" && Number.isFinite(x);

/**
 * The stretches a recording holds: its span (`from`..`to`) less its gaps.
 * @param {{from?: number, to?: number, gaps?: Array<{from: number, to: number}>}|null} rec  timeline.recordings[kind]
 * @returns {Array<{from: number, to: number}>} oldest first
 */
export function recordedRuns(rec) {
  if (!rec || !isNum(rec.from) || !isNum(rec.to) || rec.to <= rec.from) return [];
  const gaps = (rec.gaps || []).filter((g) => g && isNum(g.from) && isNum(g.to) && g.to > g.from)
    .slice().sort((a, b) => a.from - b.from);
  const runs = [];
  let from = rec.from;
  gaps.forEach((g) => {
    if (g.from > from) runs.push({ from, to: Math.min(g.from, rec.to) });
    from = Math.max(from, g.to);
  });
  if (rec.to > from) runs.push({ from, to: rec.to });
  return runs;
}

/**
 * Where the recording holding a moment starts, so a lead-up never lands before it; null when no run holds it.
 * @param {Array<{from: number, to: number}>} runs  recordedRuns()
 * @param {number} at
 */
export function floorAt(runs, at) {
  const run = (runs || []).find((r) => at >= r.from && at <= r.to);
  return run ? run.from : null;
}

/**
 * @param {Object} props
 * @param {number} props.length  the round's length in seconds
 * @param {number|null} [props.start]  where the clock rests when the address names no moment: the first
 *   recorded moment, so the room does not open on a stretch before the recording began
 * @param {React.ReactNode} props.children
 */
export function ReviewClockProvider({ length, start = null, children }) {
  const history = useHistory();
  const location = useLocation();
  const addr = readAddress(location.search).t;
  const L = Math.max(0, Number(length) || 0);
  const rest = clamp(isNum(start) ? start : 0, 0, L);
  const restRef = useRef(rest);
  restRef.current = rest;

  const [t, setT] = useState(() => clamp(addr ?? rest, 0, L));
  const [playing, setPlaying] = useState(false);
  const [rate, setRateState] = useState(1);
  const [engaged, setEngaged] = useState(addr != null);
  const [hasDriver, setHasDriver] = useState(false);

  const now = useRef(t);
  const lengthRef = useRef(L);
  lengthRef.current = L;
  const playingRef = useRef(false);
  const rateRef = useRef(1);
  const driver = useRef(null);
  const segmentEnd = useRef(null);
  /* the whole second this view last wrote to (or read from) the address */
  const written = useRef(addr);

  const setTime = useCallback((x) => {
    now.current = x;
    setT(x);
  }, []);

  const write = useCallback((at, { replace = false, extra = {} } = {}) => {
    const loc = history.location;
    const x = Math.max(0, Math.round(at));
    written.current = x;
    const next = { pathname: loc.pathname, search: searchWith(loc.search, { view: VIEW, t: clockOf(x), ...extra }), hash: loc.hash };
    if (replace) history.replace(next);
    else history.push(next);
  }, [history]);

  const stop = useCallback(() => {
    playingRef.current = false;
    segmentEnd.current = null;
    setPlaying(false);
    if (driver.current) driver.current.pause();
  }, []);

  const pause = useCallback(({ write: w = true } = {}) => {
    if (!playingRef.current) return;
    stop();
    if (w) write(now.current, { replace: true });
  }, [stop, write]);

  const play = useCallback(() => {
    setEngaged(true);
    if (now.current >= lengthRef.current - 0.05) {
      setTime(restRef.current);
      if (driver.current) driver.current.seek(restRef.current);
    }
    playingRef.current = true;
    setPlaying(true);
    if (driver.current) driver.current.play();
  }, [setTime]);

  const toggle = useCallback(() => (playingRef.current ? pause() : play()), [pause, play]);

  /**
   * Moves the clock. An explicit seek: a new history entry.
   * @param {number} at seconds
   * @param {Object} [o]
   * @param {boolean} [o.lead]  start LEAD seconds before (a flag's lead-up)
   * @param {boolean} [o.play]  true plays from there, false pauses; left out keeps playing or paused
   * @param {number} [o.until]  pause when the clock reaches this (a segment)
   * @param {string|null} [o.item]  the item picked (null clears it); left out keeps it
   * @param {boolean} [o.write] false leaves the address alone
   * @param {number|null} [o.floor] the lead-up stops here (where the recording holding `at` starts); ignored past `at`
   */
  const seek = useCallback((at, o = {}) => {
    const a = Number(at) || 0;
    const lo = isNum(o.floor) && o.floor <= a ? o.floor : 0;
    const x = clamp(a - (o.lead ? LEAD : 0), lo, lengthRef.current);
    setTime(x);
    setEngaged(true);
    if (driver.current) driver.current.seek(x);
    if (o.play === false) stop();
    if (o.write !== false) write(x, { extra: o.item !== undefined ? { item: o.item } : {} });
    if (o.play) {
      play();
      segmentEnd.current = Number.isFinite(o.until) ? o.until : null;
    }
  }, [play, setTime, stop, write]);

  /** Plays [from, to] and pauses at `to`: a flag's audio or a quote. */
  const playSegment = useCallback((from, to, o = {}) => {
    seek(from, { ...o, play: true, until: Math.max(Number(to) || 0, (Number(from) || 0) + 0.5) });
  }, [seek]);

  const setRate = useCallback((r) => {
    rateRef.current = r;
    setRateState(r);
    if (driver.current) driver.current.setRate(r);
  }, []);

  /** Sets address params without moving the clock (replace keeps history clean). */
  const setAddress = useCallback((set, { replace = true } = {}) => {
    const loc = history.location;
    const next = { pathname: loc.pathname, search: searchWith(loc.search, set), hash: loc.hash };
    if (replace) history.replace(next);
    else history.push(next);
  }, [history]);

  /** The driver's position, from timeupdate and its animation frame. Never writes the address. */
  const report = useCallback((x) => {
    const y = clamp(Number(x) || 0, 0, lengthRef.current);
    if (Math.abs(y - now.current) >= 0.04) setTime(y);
    if (segmentEnd.current != null && y >= segmentEnd.current) pause();
  }, [pause, setTime]);

  /** The driver ran out of media. */
  const reportEnded = useCallback(() => pause(), [pause]);

  /**
   * The media element that drives the clock. It is brought to the clock's moment, speed and state.
   * @returns {Function} unregister
   */
  const register = useCallback((api) => {
    driver.current = api;
    setHasDriver(true);
    api.setRate(rateRef.current);
    api.seek(now.current);
    if (playingRef.current) api.play();
    return () => {
      if (driver.current === api) {
        driver.current = null;
        setHasDriver(false);
      }
    };
  }, []);

  /* Back / Forward: the address names another moment than the one this view last wrote */
  useEffect(() => {
    if (addr === written.current) return;
    written.current = addr;
    playingRef.current = false;
    segmentEnd.current = null;
    setPlaying(false);
    const x = clamp(addr ?? restRef.current, 0, lengthRef.current);
    setTime(x);
    setEngaged(addr != null);
    if (driver.current) {
      driver.current.pause();
      driver.current.seek(x);
    }
  }, [addr, setTime]);

  /* nothing recorded: a timer moves the clock */
  useEffect(() => {
    if (!playing || hasDriver) return undefined;
    const id = setInterval(() => {
      const x = now.current + (TICK_MS / 1000) * rateRef.current;
      if (x >= lengthRef.current) {
        report(lengthRef.current);
        pause();
        return;
      }
      report(x);
    }, TICK_MS);
    return () => clearInterval(id);
  }, [playing, hasDriver, report, pause]);

  const value = useMemo(() => ({
    t, length: L, playing, rate, engaged, hasDriver,
    now: () => now.current,
    seek, play, pause, toggle, setRate, playSegment, setAddress, register, report, reportEnded,
    /** the evidence for a moment is lit while the clock is within LEAD seconds of it */
    near: (at, dur = 0) => engaged && t >= at - 1 && t <= at + Math.max(LEAD, dur || 0),
  }), [t, L, playing, rate, engaged, hasDriver, seek, play, pause, toggle, setRate, playSegment, setAddress, register, report, reportEnded]);

  return <ClockContext.Provider value={value}>{children}</ClockContext.Provider>;
}
